import { Component, OnInit } from "@angular/core";
import { CommonModule } from "@angular/common";
import { FormsModule } from "@angular/forms";
import { LucideAngularModule } from "lucide-angular";
import { TabViewModule } from "primeng/tabview";
import { TableModule } from "primeng/table";
import { InputTextModule } from "primeng/inputtext";
import { ButtonModule } from "primeng/button";
import { CardModule } from "primeng/card";
import {
  CreateFriendshipPayload,
  FriendProfile,
  Friendship,
  FriendshipStatus,
} from "../../model/friends";
import { FriendsService } from "../../services/friends.service";
import { UserService } from "../../services/user.service";
import { AuthenticationService } from "../../services/authentication.service";

type PendingRequest = {
  profile: FriendProfile;
  friendship: Friendship;
};

@Component({
  selector: "app-friendships",
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    TabViewModule,
    TableModule,
    InputTextModule,
    ButtonModule,
    CardModule,
    LucideAngularModule,
  ],
  templateUrl: "./friendships.component.html",
  styleUrl: "./friendships.component.css",
})
export class FriendshipsComponent implements OnInit {
  currentUser: any;
  private currentUserId = 0;
  friends: FriendProfile[] = [];
  incomingRequests: PendingRequest[] = [];
  outgoingRequests: PendingRequest[] = [];
  username: string = "";
  shareLink: string = "";
  qrCodeUrl: string = "";
  message: string = "";
  messageType: "success" | "error" | "" = "";
  loading = false;
  sending = false;
  readonly friendshipStatus = FriendshipStatus;
  private allFriendships: Friendship[] = [];
  activeTab: "friends" | "requests" | "add" = "friends";

  constructor(
    private friendsService: FriendsService,
    private userService: UserService,
    private authService: AuthenticationService,
  ) {}

  async ngOnInit(): Promise<void> {
    await this.authService.waitForInitialization();
    const user = this.authService.currentUserValue;
    this.currentUserId = this.normalizeId(user?.id);
    this.currentUser = user
      ? { ...user, id: this.currentUserId }
      : { id: 0, username: "", name: "", last_name: "" };
    this.updateShareLink(this.currentUserId);

    if (this.currentUserId) {
      this.loadFriendships();
    }
  }

  private loadFriendships(): void {
    if (!this.currentUserId) {
      return;
    }

    this.loading = true;
    this.friendsService.getFriendshipsForUser(this.currentUserId).subscribe({
      next: (friendships: Friendship[]) => {
        this.loading = false;
        this.processFriendships(friendships || []);
      },
      error: (error) => {
        this.loading = false;
        console.error("Failed to load friendships", error);
        this.setMessage("Failed to load friendships.", "error");
      },
    });
  }

  private processFriendships(friendships: Friendship[]): void {
    const normalizedFriendships = friendships
      .filter((friendship): friendship is Friendship => !!friendship)
      .map((friendship) => ({
        ...friendship,
        status: this.normalizeStatus(friendship.status),
      }));

    this.allFriendships = normalizedFriendships;
    this.friends = [];
    this.incomingRequests = [];
    this.outgoingRequests = [];

    for (const friendship of normalizedFriendships) {
      if (friendship.status === FriendshipStatus.Approved) {
        const profile = this.extractCounterpart(friendship);
        if (profile) {
          this.friends.push(profile);
        }
        continue;
      }

      if (friendship.status === FriendshipStatus.Pending) {
        if (friendship.friend_id === this.currentUserId) {
          const profile = this.toProfile(friendship.user);
          if (profile) {
            this.incomingRequests.push({ profile, friendship });
          }
        } else if (friendship.user_id === this.currentUserId) {
          const profile = this.toProfile(friendship.friend);
          if (profile) {
            this.outgoingRequests.push({ profile, friendship });
          }
        }
        continue;
      }

      if (friendship.status === FriendshipStatus.Denied) {
        if (friendship.user_id === this.currentUserId) {
          const profile = this.toProfile(friendship.friend);
          if (profile) {
            this.outgoingRequests.push({ profile, friendship });
          }
        }
      }
    }
  }

  private extractCounterpart(friendship: Friendship): FriendProfile | null {
    if (!this.currentUserId) {
      return null;
    }

    if (friendship.user_id !== this.currentUserId) {
      return this.toProfile(friendship.user);
    }

    if (friendship.friend_id !== this.currentUserId) {
      return this.toProfile(friendship.friend);
    }

    return null;
  }

  private toProfile(data?: FriendProfile | null): FriendProfile | null {
    if (!data || typeof data.id !== "number") {
      return null;
    }

    const username = this.sanitizeString(data.username);
    const name = this.sanitizeString(data.name);
    const lastName = this.sanitizeString(data.last_name);
    const email = this.sanitizeString(data.email);
    const phoneNumber = this.sanitizeString(data.phone_number);

    return {
      id: data.id,
      username,
      name,
      last_name: lastName,
      email,
      phone_number: phoneNumber,
      pp_points: this.normalizePoints(data.pp_points),
      profile_image: data.profile_image,
      displayName: this.buildDisplayName({
        ...data,
        username,
        name,
        last_name: lastName,
      }),
    };
  }

  getDisplayName(profile: FriendProfile | null | undefined): string {
    if (!profile) {
      return "Unknown user";
    }

    return profile.displayName ?? this.buildDisplayName(profile);
  }

  getUsername(profile: FriendProfile | null | undefined): string {
    return this.sanitizeString(profile?.username) ?? "—";
  }

  getEmail(profile: FriendProfile | null | undefined): string {
    return this.sanitizeString(profile?.email) ?? "—";
  }

  getPhone(profile: FriendProfile | null | undefined): string {
    return this.sanitizeString(profile?.phone_number) ?? "—";
  }

  getPoints(profile: FriendProfile | null | undefined): string | number {
    const points = this.normalizePoints(profile?.pp_points);
    return typeof points === "number" ? points : "—";
  }

  private setMessage(
    message: string,
    type: "success" | "error" = "success",
  ): void {
    this.message = message;
    this.messageType = type;
  }

  addFriend(): void {
    if (this.sending) {
      return;
    }

    const trimmed = this.username.trim();
    if (!trimmed) {
      this.setMessage("Please enter a username.", "error");
      return;
    }

    if (!this.currentUserId) {
      this.setMessage("Unable to send friend request.", "error");
      return;
    }

    this.sending = true;
    this.userService.getUserByUsername(trimmed).subscribe({
      next: (target) => {
        if (!target || typeof target.id !== "number") {
          this.sending = false;
          this.setMessage("User not found.", "error");
          return;
        }

        if (target.id === this.currentUserId) {
          this.sending = false;
          this.setMessage("You cannot add yourself.", "error");
          return;
        }

        if (this.isFriendOrPending(target.id)) {
          this.sending = false;
          this.setMessage("Friendship already exists or is pending.", "error");
          return;
        }

        const payload: CreateFriendshipPayload = {
          user_id: this.currentUserId,
          friend_id: target.id,
          status: FriendshipStatus.Pending,
          created_at: new Date().toISOString(),
        };

        this.friendsService.createFriendship(payload).subscribe({
          next: () => {
            this.sending = false;
            this.setMessage("Friend request sent.");
            this.username = "";
            this.loadFriendships();
          },
          error: (error) => {
            this.sending = false;
            console.error("Failed to send friend request", error);
            this.setMessage("Failed to send friend request.", "error");
          },
        });
      },
      error: (error) => {
        this.sending = false;
        console.error("Failed to search for user", error);
        this.setMessage("Unable to find user.", "error");
      },
    });
  }

  private isFriendOrPending(userId: number): boolean {
    return this.allFriendships.some((friendship) => {
      const involvesUser =
        (friendship.user_id === this.currentUserId &&
          friendship.friend_id === userId) ||
        (friendship.friend_id === this.currentUserId &&
          friendship.user_id === userId);

      if (!involvesUser) {
        return false;
      }

      return friendship.status !== FriendshipStatus.Denied;
    });
  }

  respondToRequest(request: PendingRequest, status: FriendshipStatus): void {
    const payload: Friendship = {
      ...request.friendship,
      status,
    };

    this.friendsService.updateFriendship(payload).subscribe({
      next: () => {
        if (status === FriendshipStatus.Approved) {
          this.setMessage("Friend request approved.");
        } else {
          this.setMessage("Friend request declined.");
        }
        this.loadFriendships();
      },
      error: (error) => {
        console.error("Failed to update friend request", error);
        this.setMessage("Failed to update friend request.", "error");
      },
    });
  }

  invite(friendId: number): void {
    console.log("Invite friend", friendId);
  }

  copyLink(): void {
    if (navigator.clipboard && this.shareLink) {
      navigator.clipboard.writeText(this.shareLink);
      this.setMessage("Link copied");
    }
  }

  private updateShareLink(userId: number): void {
    if (!userId) {
      this.shareLink = "";
      this.qrCodeUrl = "";
      return;
    }

    this.shareLink = `https://reball.rs/friend/${userId}`;
    this.qrCodeUrl = this.buildQrCodeUrl(this.shareLink);
  }

  private buildQrCodeUrl(link: string): string {
    if (!link) {
      return "";
    }

    const encoded = encodeURIComponent(link);
    return `https://api.qrserver.com/v1/create-qr-code/?size=180x180&data=${encoded}`;
  }

  private normalizeId(value: unknown): number {
    if (typeof value === "number" && Number.isFinite(value)) {
      return value;
    }

    if (typeof value === "string" && value.trim() !== "") {
      const parsed = Number(value);
      return Number.isFinite(parsed) ? parsed : 0;
    }

    return 0;
  }

  private sanitizeString(value: unknown): string | undefined {
    if (typeof value === "string") {
      const trimmed = value.trim();
      if (trimmed !== "") {
        return trimmed;
      }
    }

    return undefined;
  }

  private normalizePoints(value: unknown): number | undefined {
    if (typeof value === "number" && Number.isFinite(value)) {
      return value;
    }

    if (typeof value === "string" && value.trim() !== "") {
      const parsed = Number(value);
      return Number.isFinite(parsed) ? parsed : undefined;
    }

    return undefined;
  }

  private buildDisplayName(
    profile: Partial<FriendProfile> | null | undefined,
  ): string {
    if (!profile) {
      return "Unknown user";
    }

    const nameParts = [profile.name, profile.last_name]
      .map((part) => this.sanitizeString(part))
      .filter((part): part is string => !!part);

    if (nameParts.length) {
      return nameParts.join(" ");
    }

    const username = this.sanitizeString(profile.username);
    if (username) {
      return username;
    }

    if (typeof profile.id === "number" && Number.isFinite(profile.id)) {
      return `User #${profile.id}`;
    }

    return "Unknown user";
  }

  private normalizeStatus(value: unknown): FriendshipStatus {
    if (typeof value === "string") {
      const normalized = value.trim().toLowerCase();
      if (normalized === FriendshipStatus.Approved.toLowerCase()) {
        return FriendshipStatus.Approved;
      }
      if (normalized === FriendshipStatus.Pending.toLowerCase()) {
        return FriendshipStatus.Pending;
      }
      if (normalized === FriendshipStatus.Denied.toLowerCase()) {
        return FriendshipStatus.Denied;
      }
    } else if (
      value === FriendshipStatus.Approved ||
      value === FriendshipStatus.Pending ||
      value === FriendshipStatus.Denied
    ) {
      return value;
    }

    return FriendshipStatus.Pending;
  }

  setActiveTab(tab: "friends" | "requests" | "add"): void {
    this.activeTab = tab;
  }

  viewProfile(friendId: number): void {
    console.log("View profile", friendId);
  }
}
